// resources/js/admin/components/shared/media/MediaRow.tsx

import { Media } from "../../../types/features/Media";
import TableRow from "../../ui/TableRow";
import { Image, Video, File, ExternalLink } from "lucide-react";

interface MediaRowProps {
    media: Media;
    onAction: (media: Media) => void;
    onSelect?: (media: Media, isSelected: boolean) => void;
    isSelected?: boolean;
    selectable?: boolean;
}

export default function MediaRow({
    media,
    onAction,
    onSelect,
    isSelected = false,
    selectable = false,
}: MediaRowProps) {
    const getIcon = () => {
        if (media.mime_type.startsWith('image/')) {
            return Image;
        }
        if (media.mime_type.startsWith('video/')) {
            return Video;
        }
        return File;
    };

    const Icon = getIcon();

    const getFileSize = (bytes: number) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    const getStatusBadge = () => {
        return media.is_public
            ? { label: "Public", className: "bg-green-100 text-green-700" }
            : { label: "Privé", className: "bg-gray-100 text-gray-500" };
    };

    const statusBadge = getStatusBadge();

    return (
        <TableRow
            item={media}
            onAction={onAction}
            onSelect={onSelect}
            isSelected={isSelected}
            selectable={selectable}
            actionable={true}
        >
            <td className="py-3 px-4">
                <div className="flex items-center gap-3">
                    {media.mime_type.startsWith('image/') ? (
                        <img
                            src={media.url}
                            alt={media.alt_text || media.filename}
                            className="w-12 h-12 rounded-lg object-cover"
                        />
                    ) : (
                        <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                            <Icon className="w-6 h-6 text-gray-400" />
                        </div>
                    )}
                    <div>
                        <span className="font-medium text-gray-800 block truncate max-w-[200px]">
                            {media.filename}
                        </span>
                        <span className="text-xs text-gray-500">{media.mime_type}</span>
                    </div>
                </div>
            </td>

            <td className="py-3 px-4">
                <span className="text-gray-600 text-sm">{getFileSize(media.size)}</span>
            </td>

            <td className="hidden sm:table-cell py-3 px-4">
                {media.width && media.height ? (
                    <span className="text-gray-600 text-sm">
                        {media.width} × {media.height}
                    </span>
                ) : (
                    <span className="text-gray-400 text-sm">-</span>
                )}
            </td>

            <td className="hidden md:table-cell py-3 px-4">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusBadge.className}`}>
                    {statusBadge.label}
                </span>
            </td>

            <td className="hidden lg:table-cell py-3 px-4">
                <a
                    href={media.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline flex items-center gap-1 text-sm"
                >
                    Voir <ExternalLink size={12} />
                </a>
            </td>

            <td className="hidden xl:table-cell py-3 px-4">
                <span className="text-gray-500 text-sm">
                    {new Date(media.created_at).toLocaleDateString("fr-FR")}
                </span>
            </td>
        </TableRow>
    );
}